import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function CumulativePerformance({ equityCurve = [] }) {
  // Daily cumulative = raw equity curve
  const dailyData = equityCurve;

  // Weekly cumulative = aggregate by week
  const weeklyData = useMemo(() => {
    if (!equityCurve || equityCurve.length === 0) return [];
    const weeks = {};
    equityCurve.forEach(d => {
      if (!d.date) return;
      const dt = new Date(d.date);
      // Get week start (Monday)
      const day = dt.getDay();
      const diff = dt.getDate() - day + (day === 0 ? -6 : 1);
      const weekStart = new Date(dt.setDate(diff));
      const key = weekStart.toISOString().split('T')[0];
      weeks[key] = d.equity;
    });
    return Object.entries(weeks).map(([date, equity]) => ({ date, equity }));
  }, [equityCurve]);

  // Monthly cumulative = aggregate by month
  const monthlyData = useMemo(() => {
    if (!equityCurve || equityCurve.length === 0) return [];
    const months = {};
    equityCurve.forEach(d => {
      if (!d.date) return;
      const key = d.date.substring(0, 7); // YYYY-MM
      months[key] = d.equity;
    });
    return Object.entries(months).map(([date, equity]) => ({ date, equity }));
  }, [equityCurve]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <MiniCumulativeChart title="Daily Cumulative" data={dailyData} color="#378ADD" />
      <MiniCumulativeChart title="Weekly Cumulative" data={weeklyData} color="#00ff88" />
      <MiniCumulativeChart title="Monthly Cumulative" data={monthlyData} color="#a855f7" />
    </div>
  );
}

function MiniCumulativeChart({ title, data, color }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-[#0d1117] border border-[#21262d] rounded-xl p-4">
        <p className="text-[#e6edf3] text-xs font-medium mb-2">{title}</p>
        <div className="h-[120px] flex items-center justify-center">
          <p className="text-[#484f58] text-xs">No data</p>
        </div>
      </div>
    );
  }

  // Y-axis range
  const values = data.map(d => d.equity || 0);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);

  return (
    <div className="bg-[#0d1117] border border-[#21262d] rounded-xl p-4">
      <p className="text-[#e6edf3] text-xs font-medium mb-2">{title}</p>
      <ResponsiveContainer width="100%" height={140}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`cumGrad-${title.replace(/\s/g,'')}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid horizontal={true} vertical={false} strokeDasharray="3 3" stroke="#21262d" />
          <XAxis
            dataKey="date"
            stroke="#8b949e"
            tick={{ fill: '#8b949e', fontSize: 9 }}
            tickFormatter={v => { if (!v) return ''; const d = new Date(v); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }}
            interval="preserveStartEnd"
          />
          <YAxis
            stroke="#8b949e"
            tick={{ fill: '#8b949e', fontSize: 9 }}
            tickFormatter={v => v === 0 ? '$0' : `$${(v/1000).toFixed(0)}K`}
            domain={[minVal * 0.95, maxVal * 1.05]}
            width={45}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#161b22', border: '1px solid #21262d', borderRadius: '8px', fontSize: 11 }}
            formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Equity']}
            labelFormatter={(l) => l}
          />
          <Area
            type="monotone"
            dataKey="equity"
            stroke={color}
            fill={`url(#cumGrad-${title.replace(/\s/g,'')})`}
            strokeWidth={1.5}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
